'use client';
import React, { useState, useEffect, useCallback } from 'react';

interface Slide {
  id: number;
  image: string;
  alt: string;
  headline: string;
  subheadline: string;
  cta: string;
  ctaHref: string;
  accentColor: string;
}

const slides: Slide[] = [
{
  id: 1,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1097c4409-1774514615488.png",
  alt: 'Aerial view of premium residential plot development with green landscaping',
  headline: 'Upscaling Wealth.\nTransforming New Horizons.',
  subheadline: 'Premium plot development and real estate solutions for discerning investors.',
  cta: 'Explore Projects',
  ctaHref: '#projects',
  accentColor: 'bg-brand-green'
},
{
  id: 2,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_172401503-1774548188721.png",
  alt: 'Organized residential plots with modern infrastructure and wide roads',
  headline: 'Invest in Plots.\nBuild Your Legacy.',
  subheadline: 'Legally verified plots with clear documentation and expert guidance.',
  cta: 'View Projects',
  ctaHref: '#projects',
  accentColor: 'bg-brand-turmeric'
},
{
  id: 3,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19aba5f94-1786115410702.png",
  alt: 'Real estate professionals and HNI investors reviewing premium plot development plans',
  headline: 'Expert Minds.\nPremium Returns.',
  subheadline: 'A conglomerate of financial experts, engineers & HNIs delivering value-driven solutions.',
  cta: 'Learn More',
  ctaHref: '#about',
  accentColor: 'bg-brand-green'
}];


export default function HeroSlider() {
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrent(index);
    setTimeout(() => setIsAnimating(false), 600);
  }, [isAnimating]);

  const prev = () => goTo((current - 1 + slides.length) % slides.length);
  const next = useCallback(() => goTo((current + 1) % slides.length), [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <section className="relative w-full h-screen min-h-[500px] max-h-[700px] overflow-hidden">
      {/* Slides */}
      {slides.map((slide, index) =>
      <div
        key={slide.id}
        className={`absolute inset-0 transition-opacity duration-700 ${
        index === current ? 'opacity-100 z-10' : 'opacity-0 z-0'}`
        }>
        
          {/* Background Image */}
          <img
          src={slide.image}
          alt={slide.alt}
          className="w-full h-full object-cover" />
        
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black/30" />

          {/* Content Bubble */}
          <div className="absolute inset-0 flex items-center justify-end pr-8 md:pr-16 lg:pr-24">
            <div
            className={`${slide.accentColor} rounded-[30px] px-5 py-6 md:px-8 md:py-8 max-w-[220px] md:max-w-xs lg:max-w-sm text-white shadow-2xl`}
            style={{ clipPath: 'ellipse(100% 90% at 60% 50%)' }}>
            
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold font-avenir leading-tight whitespace-pre-line mb-3">
                {slide.headline}
              </h1>
              <p className="text-sm md:text-base font-avenir opacity-90 mb-4">
                {slide.subheadline}
              </p>
              <a
              href={slide.ctaHref}
              className="inline-flex items-center px-4 py-2 bg-white text-brand-dark font-semibold rounded-lg font-avenir text-xs md:text-sm hover:bg-brand-turmeric-light hover:text-white transition-colors duration-200">
              
                {slide.cta}
                <svg className="ml-2 w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Prev/Next Arrows */}
      <button
        onClick={prev}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 text-white flex items-center justify-center transition-all duration-200 backdrop-blur-sm"
        aria-label="Previous slide">
        
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        onClick={next}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 text-white flex items-center justify-center transition-all duration-200 backdrop-blur-sm"
        aria-label="Next slide">
        
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Dot Indicators */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {slides.map((_, index) =>
        <button
          key={index}
          onClick={() => goTo(index)}
          className={`transition-all duration-300 rounded-full ${
          index === current ?
          'w-8 h-3 bg-brand-turmeric' : 'w-3 h-3 bg-white/60 hover:bg-white'}`
          }
          aria-label={`Go to slide ${index + 1}`} />

        )}
      </div>

      {/* Yellow accent strip (right side) */}
      <div className="absolute right-0 top-0 bottom-0 w-3 bg-brand-turmeric z-20" />
    </section>);

}